import {
  findRecoveryContentOverlap,
  normalizeRecoverySnapshotContent,
  sanitizeArtifactDraftContent,
} from './recoveryContent';

/** Conteúdo menor que isto não é avaliado como replay duplicado. */
const REPLAY_MIN_LENGTH = 220;
/** Âncora do início do conteúdo: fração do tamanho, entre o mínimo e o máximo. */
const REPLAY_ANCHOR_RATIO = 0.2;
const REPLAY_ANCHOR_MIN = 70;
const REPLAY_ANCHOR_MAX = 180;
/** Âncora curta demais (após trim) não identifica replay. */
const REPLAY_ANCHOR_MIN_TRIMMED = 50;
/** Prefixo comum mínimo para tratar o incoming como reescrita do current. */
const MIN_SHARED_PREFIX = 80;
/** Incoming com ao menos esta fração do current é uma versão mais completa. */
const INCOMING_GROWTH_RATIO = 0.8;
/** Versão limpa de um replay duplicado mantém ao menos esta fração. */
const REPLAY_CLEANUP_RATIO = 0.45;

function countSubstringOccurrences(source: string, needle: string): number {
  if (!source || !needle) {
    return 0;
  }

  let count = 0;
  let startIndex = 0;
  while (startIndex < source.length) {
    const matchIndex = source.indexOf(needle, startIndex);
    if (matchIndex === -1) {
      break;
    }
    count += 1;
    startIndex = matchIndex + Math.max(1, Math.floor(needle.length / 2));
  }
  return count;
}

function hasReplayLikeDuplicatePrefix(content: string): boolean {
  if (!content || content.length < REPLAY_MIN_LENGTH) {
    return false;
  }

  const anchorLength = Math.min(
    REPLAY_ANCHOR_MAX,
    Math.max(
      REPLAY_ANCHOR_MIN,
      Math.floor(content.length * REPLAY_ANCHOR_RATIO),
    ),
  );
  const anchor = content.slice(0, anchorLength).trim();
  if (anchor.length < REPLAY_ANCHOR_MIN_TRIMMED) {
    return false;
  }

  return countSubstringOccurrences(content, anchor) > 1;
}

function getSharedPrefixLength(a: string, b: string): number {
  const maxLength = Math.min(a.length, b.length);
  let prefixLength = 0;
  while (
    prefixLength < maxLength &&
    a.charCodeAt(prefixLength) === b.charCodeAt(prefixLength)
  ) {
    prefixLength += 1;
  }
  return prefixLength;
}

/**
 * Replay streams can resend the full transcript with minor formatting/tool-summary
 * differences. Compare normalized bodies to avoid duplicating large blocks.
 */
function normalizeForComparison(content: string): string {
  return normalizeRecoverySnapshotContent(
    sanitizeArtifactDraftContent(content)
      .replace(/^Ferramentas:\s.*$/gim, '')
      .replace(/\[\s*Using tool:[^\]]+\]/gi, '')
      .replace(/\s+/g, ' ')
      .trim(),
  );
}

/** O current parece um replay duplicado e o incoming, a versão limpa dele. */
function incomingCleansDuplicatedReplay(
  normalizedCurrent: string,
  normalizedIncoming: string,
): boolean {
  return (
    hasReplayLikeDuplicatePrefix(normalizedCurrent) &&
    !hasReplayLikeDuplicatePrefix(normalizedIncoming) &&
    normalizedIncoming.length >=
      Math.floor(normalizedCurrent.length * REPLAY_CLEANUP_RATIO)
  );
}

/**
 * Decide pelo conteúdo NORMALIZADO qual lado prevalece: `current` (o incoming
 * já está contido), `incoming` (é uma versão mais completa ou a limpa de um
 * replay) ou `null` (nenhum — segue para a junção por overlap).
 */
function pickByNormalizedContent(
  current: string,
  incoming: string,
): 'current' | 'incoming' | null {
  const normalizedCurrent = normalizeForComparison(current);
  const normalizedIncoming = normalizeForComparison(incoming);
  if (!normalizedCurrent || !normalizedIncoming) {
    return null;
  }

  const replayCleanup = incomingCleansDuplicatedReplay(
    normalizedCurrent,
    normalizedIncoming,
  );
  if (
    normalizedCurrent === normalizedIncoming ||
    (normalizedCurrent.includes(normalizedIncoming) && !replayCleanup)
  ) {
    return 'current';
  }
  if (normalizedIncoming.includes(normalizedCurrent)) {
    return 'incoming';
  }

  const sharesPrefix =
    getSharedPrefixLength(normalizedCurrent, normalizedIncoming) >=
    MIN_SHARED_PREFIX;
  const grows =
    incoming.length >= Math.floor(current.length * INCOMING_GROWTH_RATIO);
  return sharesPrefix && (grows || replayCleanup) ? 'incoming' : null;
}

export function appendStreamingContent(
  current: string,
  incoming: string,
): string {
  if (!incoming) {
    return current;
  }
  if (!current) {
    return incoming;
  }
  if (current.includes(incoming)) {
    return current;
  }
  if (incoming.includes(current)) {
    return incoming;
  }

  const picked = pickByNormalizedContent(current, incoming);
  if (picked) {
    return picked === 'current' ? current : incoming;
  }

  const overlap = findRecoveryContentOverlap(current, incoming);
  if (overlap > 0) {
    return `${current}${incoming.slice(overlap)}`;
  }

  return `${current}${incoming}`;
}
